/**
 * ContactCaptureCard — the locked "Contact Capture" slot interleaved among the
 * questions (WCR parity). Its position in the list IS
 * settings.contact_capture_position: front-load qualifying questions, then
 * capture contact info. Drag it (or use the up/down buttons) to move it.
 *
 * This only affects the PUBLIC website lead flow (the WCR Next.js site renders
 * this form and collects the contact here). Recruiter-texted /f/ links skip it
 * entirely — the patient is already a known contact — so the note makes that
 * explicit rather than implying texted patients re-enter their name.
 */
import { Lock, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

type ContactMethod = 'phone' | 'email';

interface ContactCaptureCardProps {
  contactMethod: ContactMethod;
  onContactMethodChange: (m: ContactMethod) => void;
  canEdit: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

export default function ContactCaptureCard({
  contactMethod, onContactMethodChange, canEdit, onMoveUp, onMoveDown, canMoveUp, canMoveDown,
}: ContactCaptureCardProps) {
  const toggle = (value: ContactMethod, label: string, side: 'l' | 'r') => (
    <button
      type="button"
      disabled={!canEdit}
      onClick={() => onContactMethodChange(value)}
      className={`${side === 'l' ? 'rounded-l-lg' : 'rounded-r-lg'} px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${
        contactMethod === value
          ? 'bg-primary-600 text-white'
          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-lg border-2 border-primary-200 bg-primary-50/40 p-3 dark:border-primary-800/60 dark:bg-primary-900/10 sm:p-4">
      <div className="flex items-start gap-2">
        {canEdit && (
          <div className="hidden cursor-grab pt-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 sm:block" title="Drag to reposition">
            <GripVertical className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-200">Contact Capture</h3>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 dark:border-gray-600 dark:bg-gray-800">First Name</div>
            <div className="rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 dark:border-gray-600 dark:bg-gray-800">Last Name</div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Contact method:</span>
            <div className="flex rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800">
              {toggle('phone', 'Phone Only', 'l')}
              {toggle('email', 'Phone + Email', 'r')}
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Shown on the public website lead form, at this position. Texted links skip this step — the patient is already a known contact.
          </p>
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-col sm:hidden">
            <button onClick={onMoveUp} disabled={!canMoveUp} className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200" aria-label="Move contact capture up">
              <ArrowUp className="h-4 w-4" />
            </button>
            <button onClick={onMoveDown} disabled={!canMoveDown} className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200" aria-label="Move contact capture down">
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
